import React, { useState, useEffect, useRef, useCallback } from "react";
import "../css/css_pages/Dashboard.css";
import Cookies from "js-cookie";
import { FiImage } from "react-icons/fi";
import { FaCheck, FaSync, FaSearch } from "react-icons/fa";
import config from "../../config";
import ChecklistPopupTSO from "./ChecklistPopupTSO";
import { useParams } from "react-router-dom";
import PopupMessage from "../layouts/PopupMessage";
import Select from "react-select";

const TSOScheduleViewer = () => {
  const { selectedDate } = useParams();
  const [token, setToken] = useState("");
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showAcceptRejectModal, setShowAcceptRejectModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("accept");
  const [comment, setComment] = useState("");
  const [proposedDate, setProposedDate] = useState("");
  const [alertMessage, setAlertMessage] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [showNotVisited, setShowNotVisited] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [note, setNote] = useState("");
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [pdfSchedule, setPdfSchedule] = useState(null);
  const [allSchedules, setAllSchedules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [tsoFilter, setTsoFilter] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    clients: [],
    tsos: [],
  });
  const [activeQuickFilter, setActiveQuickFilter] = useState("today");

  const statusSelectRef = useRef(null);
  const clientSelectRef = useRef(null);
  const tsoSelectRef = useRef(null);

  const previousDataRef = useRef({
    schedules: [],
  });

  // Apply filters to schedules
  const applyFilters = useCallback(
    (schedules) => {
      let filtered = Array.isArray(schedules) ? [...schedules] : [schedules];

      if (searchTerm) {
        filtered = filtered.filter(
          (s) =>
            s.schedule_description &&
            s.schedule_description
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
        );
      }

      if (statusFilter) {
        filtered = filtered.filter((s) => s.schedule_status === statusFilter);
      }

      if (clientFilter) {
        filtered = filtered.filter((s) => s.customer_name === clientFilter);
      }

      if (tsoFilter) {
        filtered = filtered.filter((s) => s.assigned_tso_name === tsoFilter);
      }

      return filtered;
    },
    [searchTerm, statusFilter, clientFilter, tsoFilter]
  );

  // Update filter options when allSchedules changes
  useEffect(() => {
    if (allSchedules.length > 0) {
      const statuses = Array.from(
        new Set(
          allSchedules
            .map((s) => s.schedule_status)
            .filter((status) => status && status.trim())
        )
      );

      const clients = Array.from(
        new Set(
          allSchedules
            .map((s) => s.customer_name)
            .filter((name) => name && name.trim())
        )
      );

      const tsos = Array.from(
        new Set(
          allSchedules
            .map((s) => s.assigned_tso_name)
            .filter((name) => name && name.trim())
        )
      );

      setFilterOptions({
        statuses,
        clients,
        tsos,
      });
    }
  }, [allSchedules]);

  // Apply filters when allSchedules or filter values change
  useEffect(() => {
    if (allSchedules.length > 0) {
      let toFilter = allSchedules;

      // Apply "Not Visited" filter if enabled
      if (showNotVisited) {
        toFilter = toFilter.filter(
          (schedule) =>
            schedule.schedule_status !== "completed" &&
            schedule.schedule_status !== "closed"
        );
      }

      const filtered = applyFilters(toFilter);
      setFilteredSchedules(filtered);
    }
  }, [allSchedules, applyFilters, showNotVisited]);

  // Utility function to format date in UTC without seconds and timezone
  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);

    // Format as UTC time
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC", // Use UTC instead of local time
    };

    if (!showOnlyTime) {
      options.year = "numeric";
      options.month = "2-digit";
      options.day = "2-digit";
    }

    return date.toLocaleString("en-US", options).replace(",", "");
  };

  const showPopupMessage = (message) => {
    setAlertMessage(message);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };

  const handleFileModalClose = () => {
    setShowFileModal(false);
    setSelectedFile(null);
  };

  const handleFeedback = (schedule) => {
    setSelectedSchedule(schedule);
    setShowFeedbackModal(true);
  };

  const submitFeedback = async () => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      feedback: parseInt(feedback, 10);
      console.log(feedback);

      if (feedback < 1 || feedback > 4) {
        showPopupMessage("Please select a valid feedback.");
        return;
      }
      const feedbackData = {
        schedule_id: selectedSchedule.schedule_id,
        feedback: parseInt(feedback, 10),
      };
      const response = await fetch(
        `${config.API_URL}/tso-online/add-feedback`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          method: "PUT",
          body: JSON.stringify(feedbackData),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.statusText}`);
      }

      const data = await response.json();
      showPopupMessage("Feedback submitted successfully!");
      setShowFeedbackModal(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      showPopupMessage("Failed to submit feedback.");
    }
  };

  useEffect(() => {
    const authToken = Cookies.get("authToken");
    setToken(authToken);
  }, []);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      const currentDate = new Date();
      let startDate, endDate;

      if (showNotVisited) {
        const startOfYearNotVisited = new Date(currentDate.getFullYear(), 0, 1);
        const yesterday = new Date(currentDate);
        yesterday.setDate(currentDate.getDate() - 1);
        startDate = startOfYearNotVisited.toISOString().split("T")[0];
        endDate = yesterday.toISOString().split("T")[0];
      } else {
        startDate = endDate = currentDate.toISOString().split("T")[0];
      }

      fetchSchedules(startDate, endDate);
    }
  }, [showNotVisited, token]);

  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      const currentDate = new Date();
      let startDate, endDate;

      if (showNotVisited) {
        const startOfYearNotVisited = new Date(currentDate.getFullYear(), 0, 1);
        const yesterday = new Date(currentDate);
        yesterday.setDate(currentDate.getDate() - 1);
        startDate = startOfYearNotVisited.toISOString().split("T")[0];
        endDate = yesterday.toISOString().split("T")[0];
      } else {
        startDate = endDate = currentDate.toISOString().split("T")[0];
      }

      await fetchSchedules(startDate, endDate);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Manual refresh function
  const handleRefresh = async () => {
    await fetchData();
  };

  const fetchSchedules = async (startDate, endDate) => {
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${config.API_URL}/tso-online/get-all-schedules?start_date=${startDate}&end_date=${endDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch schedules: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data || !Array.isArray(data)) {
        throw new Error("Invalid data received from the server.");
      }

      setAllSchedules(data);
      console.log("Fetched schedules:", data.length);
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setFilteredSchedules([]);
    }
  };

  const viewPDF = async (schedule_id, schedule_no, language) => {
    if (!schedule_id) {
      showPopupMessage("No schedule id found.");
      return;
    }

    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(`${config.API_URL}/client/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          schedule_id: schedule_id,
          language: language,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to view pdf: ${response.statusText}`);
      }

      const pdfBlob = await response.blob();
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const newWindow = window.open(pdfUrl, "_blank");
      if (newWindow) {
        newWindow.document.write(
          `<html><head><title>${"Checklist PDF of Schedule "}${schedule_no}</title></head><body style="margin:0;">
            <iframe src="${pdfUrl}" width="100%" height="100%" style="border:none;"></iframe>
            </body></html>`
        );
        newWindow.document.close();
      } else {
        showPopupMessage("Failed to open the PDF in a new window.");
      }
    } catch (error) {
      console.error("Error viewing pdf:", error);
      showPopupMessage("Failed to view pdf. Please try again.");
    }
  };

  const filterSchedules = (filterType) => {
    const currentDate = new Date();
    let startDate, endDate;

    switch (filterType) {
      case "selectedDate":
        startDate = endDate = new Date(selectedDate)
          .toLocaleDateString("en-CA")
          .split("T")[0];
        break;
      case "today":
        startDate = endDate = currentDate.toISOString().split("T")[0];
        break;
      case "thisWeek":
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = startOfWeek.toISOString().split("T")[0];
        endDate = endOfWeek.toISOString().split("T")[0];
        break;
      case "thisMonth":
        const startOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
        startDate = startOfMonth.toISOString().split("T")[0];
        endDate = endOfMonth.toISOString().split("T")[0];
        break;
      case "thisYear":
        const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
        const endOfYear = new Date(currentDate.getFullYear(), 11, 31);
        startDate = startOfYear.toISOString().split("T")[0];
        endDate = endOfYear.toISOString().split("T")[0];
        break;
      case "notVisited":
        setShowNotVisited(true);
        const startOfYearNotVisited = new Date(currentDate.getFullYear(), 0, 1);
        const yesterday = new Date(currentDate);
        yesterday.setDate(currentDate.getDate() - 1);
        startDate = startOfYearNotVisited.toISOString().split("T")[0];
        endDate = yesterday.toISOString().split("T")[0];
        break;
      default:
        startDate = endDate = currentDate.toISOString().split("T")[0];
    }

    fetchSchedules(startDate, endDate);
  };

  const openResource = async (resource, issue) => {
    setSelectedSchedule(issue);

    const url = `${config.API_URL}/client/view-schedule-image?schedule_id=${issue.schedule_id}&file_name=${resource}`;
    const token = Cookies.get("authToken");
    if (!token) {
      throw new Error("Authentication token not found.");
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const fileType = blob.type;
        const file = new File([blob], resource, { type: fileType });

        setSelectedFile({
          name: resource,
          type: fileType,
          blob: URL.createObjectURL(blob),
        });
        setShowFileModal(true);
      } else {
        console.error("Failed to fetch the resource:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching the resource:", error);
    }
  };

  const closeImageModal = () => {
    setImageSrc(null);
    setSelectedSchedule(null);
  };

  const handleCheck = (issue) => {
    setSelectedSchedule(issue);
    setShowChecklist(true);
  };

  const closeChecklistPopup = () => {
    setShowChecklist(false);
    setSelectedSchedule(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return { backgroundColor: "#faafaf", color: "#8B0000" };
      case "in_progress":
        return { backgroundColor: "#fff3cd", color: "#856404" };
      case "confirmed":
        return { backgroundColor: "#cce5ff", color: "#004085" };
      case "completed":
        return { backgroundColor: "#d4edda", color: "#155724" };
      case "proposed":
        return { backgroundColor: "#EFEFFF", color: "#871F78" };
      case "closed":
        return { backgroundColor: "#d1d1d1", color: "#333333" };
      default:
        return { backgroundColor: "#f8f9fa", color: "#212529" };
    }
  };

  const handleSubmit = async () => {
    try {
      const updateData = {
        schedule_id: selectedSchedule.schedule_id,
        status: selectedStatus,
        comment,
        proposed_date: proposedDate,
      };

      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(`${config.API_URL}/client/update-schedule`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update schedule: ${response.statusText}`);
      }

      const data = await response.json();
      showPopupMessage("Schedule updated successfully!");
      setShowAcceptRejectModal(false);
    } catch (error) {
      console.error("Error updating schedule:", error);
      showPopupMessage("Failed to update schedule.");
    }
  };

  const handleNoteSubmit = () => {
    console.log("Note submitted:", note);
    setShowNoteModal(false);
    setNote("");
  };

  const handleLanguageSelect = (event) => {
    const selectedLang = event.target.value;

    // Check if a valid language (not the placeholder) was selected
    if (selectedLang) {
      setSelectedLanguage(selectedLang);
      setShowLanguageModal(false);
      viewPDF(pdfSchedule.schedule_id, pdfSchedule.schedule_no, selectedLang);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Schedules</h1>
        <div className="header-controls">
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="refresh-button"
            disabled={isRefreshing}
          >
            <FaSync className={isRefreshing ? "spin-icon" : ""} />
          </button>
        </div>
      </div>

      <div className="modern-filter-section">
        <div className="filter-header">
          <h3 className="filter-title">Quick Filters</h3>
          <div className="quick-buttons">
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "today" ? "active" : ""
              }`}
              onClick={() => {
                setActiveQuickFilter("today");
                setShowNotVisited(false);
                setSearchTerm("");
                setStatusFilter("");
                setClientFilter("");
                setTsoFilter("");
                if (statusSelectRef.current)
                  statusSelectRef.current.clearValue();
                if (clientSelectRef.current)
                  clientSelectRef.current.clearValue();
                if (tsoSelectRef.current) tsoSelectRef.current.clearValue();
                filterSchedules("today");
              }}
            >
              📅 Today
            </button>
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "thisWeek" ? "active" : ""
              }`}
              onClick={() => {
                setActiveQuickFilter("thisWeek");
                setShowNotVisited(false);
                setSearchTerm("");
                setStatusFilter("");
                setClientFilter("");
                setTsoFilter("");
                if (statusSelectRef.current)
                  statusSelectRef.current.clearValue();
                if (clientSelectRef.current)
                  clientSelectRef.current.clearValue();
                if (tsoSelectRef.current) tsoSelectRef.current.clearValue();
                filterSchedules("thisWeek");
              }}
            >
              📆 This Week
            </button>
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "thisMonth" ? "active" : ""
              }`}
              onClick={() => {
                setActiveQuickFilter("thisMonth");
                setShowNotVisited(false);
                setSearchTerm("");
                setStatusFilter("");
                setClientFilter("");
                setTsoFilter("");
                if (statusSelectRef.current)
                  statusSelectRef.current.clearValue();
                if (clientSelectRef.current)
                  clientSelectRef.current.clearValue();
                if (tsoSelectRef.current) tsoSelectRef.current.clearValue();
                filterSchedules("thisMonth");
              }}
            >
              📋 This Month
            </button>
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "thisYear" ? "active" : ""
              }`}
              onClick={() => {
                setActiveQuickFilter("thisYear");
                setShowNotVisited(false);
                setSearchTerm("");
                setStatusFilter("");
                setClientFilter("");
                setTsoFilter("");
                if (statusSelectRef.current)
                  statusSelectRef.current.clearValue();
                if (clientSelectRef.current)
                  clientSelectRef.current.clearValue();
                if (tsoSelectRef.current) tsoSelectRef.current.clearValue();
                filterSchedules("thisYear");
              }}
            >
              📊 This Year
            </button>
            <button
              className={`quick-filter-btn ${
                activeQuickFilter === "notVisited" ? "active" : ""
              }`}
              onClick={() => {
                setActiveQuickFilter("notVisited");
                setSearchTerm("");
                setStatusFilter("");
                setClientFilter("");
                setTsoFilter("");
                if (statusSelectRef.current)
                  statusSelectRef.current.clearValue();
                if (clientSelectRef.current)
                  clientSelectRef.current.clearValue();
                if (tsoSelectRef.current) tsoSelectRef.current.clearValue();
                filterSchedules("notVisited");
              }}
            >
              ⚠️ Not Visited
            </button>
          </div>
        </div>

        <div className="filter-controls">
          <div className="search-bar-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search schedules by description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="modern-search-input"
            />
          </div>

          <div className="advanced-filters">
            <div className="filter-select-wrapper">
              <Select
                ref={statusSelectRef}
                options={filterOptions.statuses.map((status) => ({
                  value: status,
                  label: status
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase()),
                }))}
                placeholder="Status"
                onChange={(option) => setStatusFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-select-wrapper">
              <Select
                ref={clientSelectRef}
                options={filterOptions.clients.map((client) => ({
                  value: client,
                  label: client,
                }))}
                placeholder="Client"
                onChange={(option) => setClientFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-select-wrapper">
              <Select
                ref={tsoSelectRef}
                options={filterOptions.tsos.map((tso) => ({
                  value: tso,
                  label: tso,
                }))}
                placeholder="TSO"
                onChange={(option) => setTsoFilter(option?.value || "")}
                isClearable
                className="modern-filter-select"
              />
            </div>

            <div className="filter-count-display">
              <span className="count-badge">{filteredSchedules.length}</span>
              schedule{filteredSchedules.length !== 1 ? "s" : ""}
            </div>
            <div className="filter-count-display">
              <span className="count-badge">{filteredSchedules.length}</span>
              schedule{filteredSchedules.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>

      {filteredSchedules.length > 0 ? (
        <>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Description</th>
                <th>Resources</th>
                <th>Date & Time</th>
                <th>Status</th>
                <th>Checklist</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...filteredSchedules]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((schedule) => {
                  const currentDate = new Date();
                  const scheduleDate = new Date(schedule.scheduled_date);
                  const isPastDate =
                    scheduleDate < currentDate && !isToday(scheduleDate);

                  return (
                    <tr key={schedule.schedule_id}>
                      <td>{schedule.schedule_no}</td>
                      <td style={{ maxWidth: "300px", wordWrap: "break-word" }}>
                        {schedule.schedule_description}
                      </td>
                      <td>
                        {schedule.img_file ? (
                          <>
                            {schedule.img_file
                              .split(",")
                              .map((resource, index) => (
                                <button
                                  key={index}
                                  className="resource-link"
                                  onClick={() =>
                                    openResource(resource, schedule)
                                  }
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    marginRight: "5px",
                                  }}
                                >
                                  <FiImage size={20} color="#1C3663" />
                                </button>
                              ))}
                          </>
                        ) : (
                          "No resources available"
                        )}
                      </td>
                      <td
                        style={{
                          maxWidth: "300px",
                          whiteSpace: "normal",
                          overflow: "visible",
                        }}
                      >
                        <input
                          value={formatDateTime(schedule.scheduled_date)}
                          readOnly
                          style={{
                            border: "none",
                            backgroundColor: "#cce5ff",
                            color: "black",
                            fontFamily: "inherit",
                            fontSize: "inherit",
                            width: "75%",
                            cursor: "default",
                            borderRadius: "100px",
                          }}
                        />
                      </td>

                      <td
                        style={{
                          ...getStatusColor(schedule.schedule_status),
                          padding: "5px",
                          textAlign: "center",
                        }}
                      >
                        {schedule.schedule_status
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </td>
                      <td>
                        {schedule.schedule_status === "completed" ||
                        schedule.schedule_status === "closed" ? (
                          <FaCheck
                            className="action-icon"
                            size={20}
                            color="green"
                            onClick={() => handleCheck(schedule)}
                            style={{ cursor: "pointer" }}
                            title="Click to update"
                          />
                        ) : (
                          <span
                            style={{ color: "gray", cursor: "not-allowed" }}
                            title="Not available"
                          >
                            <FaCheck size={20} />
                          </span>
                        )}
                      </td>
                      <td>
                        {schedule.schedule_status === "completed" ||
                        schedule.schedule_status === "closed" ? (
                          <>
                            <button
                              onClick={() => {
                                setPdfSchedule(schedule);
                                setShowLanguageModal(true);
                              }}
                              style={{
                                backgroundColor: "#4CAF50",
                                color: "white",
                                borderRadius: "5px",
                                border: "none",
                                cursor: "pointer",
                                marginRight: "4px",
                                padding: "5px 10px",
                                marginTop: "5px",
                              }}
                            >
                              View Checklist
                            </button>
                            <button
                              onClick={() => handleFeedback(schedule)}
                              style={{
                                backgroundColor: "#2196F3",
                                color: "white",
                                borderRadius: "5px",
                                border: "none",
                                cursor: "pointer",
                                padding: "5px 10px",
                                marginTop: "5px",
                              }}
                            >
                              Add Feedback
                            </button>
                          </>
                        ) : (
                          <>
                            {isPastDate && (
                              <button
                                onClick={() => {
                                  setSelectedSchedule(schedule);
                                  setShowNoteModal(true);
                                }}
                                style={{
                                  backgroundColor: "#FFA500",
                                  color: "white",
                                  borderRadius: "5px",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: "5px 10px",
                                  marginTop: "5px",
                                }}
                              >
                                Add Note
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </>
      ) : (
        <p>No schedules found for the selected filter.</p>
      )}

      {showAcceptRejectModal && selectedSchedule && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Update Schedule</h2>
            <label>Status:</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="accept">Accept</option>
              <option value="reject">Reject</option>
              <option value="propose">Propose</option>
            </select>

            {selectedStatus !== "accept" && (
              <>
                <label>Comment:</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Enter your comment..."
                />
              </>
            )}

            {selectedStatus === "propose" && (
              <>
                <label>Proposed Date:</label>
                <input
                  type="datetime-local"
                  value={proposedDate}
                  onChange={(e) => setProposedDate(e.target.value)}
                />
              </>
            )}

            <button onClick={handleSubmit}>Update</button>
            <span
              className="close"
              onClick={() => setShowAcceptRejectModal(false)}
            >
              &times;
            </span>
          </div>
        </div>
      )}

      {imageSrc && (
        <div className="image-modal-overlay">
          <div className="image-modal-content">
            <img src={imageSrc} alt="Resource" />
            <span className="close" onClick={closeImageModal}>
              &times;
            </span>
          </div>
        </div>
      )}

      {showChecklist && selectedSchedule && (
        <ChecklistPopupTSO
          checklist={selectedSchedule}
          onClose={closeChecklistPopup}
        />
      )}

      {showFeedbackModal && selectedSchedule && (
        <div className="feedback-modal-overlay">
          <div className="feedback-modal-content">
            <h2>Submit Feedback</h2>
            <div className="feedback-field">
              <label>Schedule No:</label>
              <span>{selectedSchedule.schedule_no}</span>
            </div>
            <div className="feedback-field">
              <label>Description:</label>
              <span>{selectedSchedule.schedule_description}</span>
            </div>
            <div className="feedback-field">
              <label>Client Name:</label>
              <span>{selectedSchedule.customer_name}</span>
            </div>
            <div className="feedback-field">
              <label>Date & Time:</label>
              <span>{formatDateTime(selectedSchedule.scheduled_date)}</span>
            </div>
            <div className="feedback-field">
              <label>Feedback:</label>
              <select
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              >
                <option value="">Select Feedback</option>
                <option value="4">😊 Best</option>
                <option value="3">🙂 Good</option>
                <option value="2">😐 Average</option>
                <option value="1">😞 Bad</option>
              </select>
            </div>
            <button onClick={submitFeedback}>Submit</button>
            <span className="close" onClick={() => setShowFeedbackModal(false)}>
              &times;
            </span>
          </div>
        </div>
      )}

      {showNoteModal && selectedSchedule && (
        <div className="note-modal-overlay">
          <div className="note-modal-content">
            <h2>Add Note</h2>
            <div className="note-field">
              <label>Client Name:</label>
              <span>{selectedSchedule.customer_name}</span>
            </div>
            <div className="note-field">
              <label>TSO Name:</label>
              <span>{selectedSchedule.assigned_tso_name}</span>
            </div>
            <div className="note-field">
              <label>Date:</label>
              <span>{formatDateTime(selectedSchedule.scheduled_date)}</span>
            </div>
            <div className="note-field">
              <label>Note:</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter your note..."
              />
            </div>
            <button onClick={handleNoteSubmit}>Submit</button>
            <span className="close" onClick={() => setShowNoteModal(false)}>
              &times;
            </span>
          </div>
        </div>
      )}

      {showFileModal && selectedFile && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close" onClick={handleFileModalClose}>
              &times;
            </span>
            <h3>File Details</h3>
            <p>Name: {selectedFile.name}</p>
            <p>Type: {selectedFile.type}</p>
            {selectedFile.type.startsWith("image/") && (
              <img
                src={selectedFile.blob}
                alt={selectedFile.name}
                style={{ maxWidth: "100%" }}
              />
            )}
            {selectedFile.type === "text/plain" && (
              <iframe
                src={selectedFile.blob}
                style={{ width: "100%", height: "200px" }}
                title="Text File Preview"
              />
            )}
          </div>
        </div>
      )}

      {showLanguageModal && pdfSchedule && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Select Language</h2>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              {/* --- REPLACED BUTTONS WITH DROPDOWN --- */}
              <select
                onChange={handleLanguageSelect} // Use the new handler
                defaultValue="" // Sets the default to the placeholder
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "1rem",
                  minWidth: "200px", // Keeps a similar width
                }}
              >
                {/* Placeholder option */}
                <option value="" disabled>
                  Select a language...
                </option>

                {/* Language options */}
                <option value="si">Sinhala</option>
                <option value="ta">Tamil</option>
              </select>
              {/* --- END OF REPLACEMENT --- */}
            </div>
            <span
              className="close"
              onClick={() => setShowLanguageModal(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "15px",
                cursor: "pointer",
              }}
            >
              &times;
            </span>
          </div>
        </div>
      )}

      {alertMessage && (
        <PopupMessage message={alertMessage} onClose={closePopupMessage} />
      )}
    </div>
  );
};

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export default TSOScheduleViewer;
